import React, {
    FC, useEffect, useMemo, useCallback, useRef,
} from 'react';
import nextId from 'react-id-generator';
import { useDispatch, useSelector } from 'react-redux';
import {
    partsSelectActions,
    apiGetActions,
} from '@app/react/action';
import { showOrderPartsHelpAction } from '@app/react/action/showOrderPartsHelp';
import { IinitState } from '@app/react/reducer';
import type { AppState } from '@app/react/reducer';
import { GetResponseOrderParts } from '@app/react/api';
import { SELECT_ALL, OrderSelectBaseLayout } from '@component/index';
import {
    filterProducts,
    reduceProductWithCategory,
    submitPartSelect,
    hideEcHeaderNavi,
} from '@root/app/react/common/Function';
import { buildCategoryLength } from '@app/react/common/buildCategoryLength';
import { buildSubCategoryLength } from '@app/react/common/buildSubCategoryLength';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { OnSelectProductHandler } from '@app/react/uikit/ProductThumbnail';
import { OrderPartsImg } from '@root/app/react/uikit';

import { OrderPartsHelpThread } from '@component/OrderPartsHelp';
import {
    DesignContainer,
    CategoryLists,
    CateogryBox,
    LayoutContextProvider,
} from '@component/OrderProductCategoryList';
import { OrderOptionContainer } from '@container/OrderOption';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { useWindowScrollPosition } from '@app/react/hooks/useWindowScrollPosition';
import { useSessionStorage } from '@app/react/hooks/useSessionStorage';
import { selectOrderPartsHelp } from './selectors';

const partsNameProps = '糸・和紙';

export const OrderWashiSelectContainer: FC<{ waitFetch?: boolean }> = ({ waitFetch = false }) => {
    const dispatch = useDispatch();
    const { arrowType, orderType } = useOrderPathParams();
    const isDesktop: boolean = useMediaDesktop();
    const isMobile: boolean = useMediaMobile();
    const htmlId = useMemo(() => nextId('order-product-category-list-'), []);
    const scrollObj = useRef(null);
    useWindowScrollPosition('scroll_washi', true, scrollObj);

    const customizeData = useSelector<AppState, IinitState>(
        (state) => state.customize,
    );
    const threadProductList = useSelector<
    AppState,
    GetResponseOrderParts | null
    >((state) => state.APIData.thread);

    const openHelpParts = useSelector(selectOrderPartsHelp);
    const isOpenHelp = openHelpParts === 'thread';

    const [selectCategoryId, setSelectCategoryId] = useSessionStorage<number>(
        'cat_washi', SELECT_ALL,
    );
    const [selectSubCategoryId, setSelectSubCategoryId] = useSessionStorage<number>(
        'subcat_washi', SELECT_ALL,
    );
    const categoryChange: React.ChangeEventHandler<HTMLSelectElement> = (
        event,
    ) => {
        setSelectCategoryId(parseInt(event.target.value, 10));
        setSelectSubCategoryId(SELECT_ALL);
    };
    const subCategoryChange: React.ChangeEventHandler<HTMLSelectElement> = (
        event,
    ) => {
        setSelectSubCategoryId(parseInt(event.target.value, 10));
    };

    const categoryData = useMemo(() => (buildCategoryLength(
        threadProductList?.categoryData ?? [],
        threadProductList?.partsData ?? [],
    )), [threadProductList]);

    const subCategoryData = useMemo(() => (buildSubCategoryLength(
        threadProductList?.categoryData ?? [],
        threadProductList?.partsData ?? [],
        selectCategoryId,
    )), [threadProductList, selectCategoryId]);

    const viewData = useMemo(() => {
        const newPartsData = filterProducts(
            threadProductList?.partsData || [],
            selectCategoryId,
            selectSubCategoryId,
        );
        return reduceProductWithCategory(newPartsData);
    }, [threadProductList, selectCategoryId, selectSubCategoryId]);

    const onSelectProduct: OnSelectProductHandler = (event, product) => {
        submitPartSelect(dispatch, partsSelectActions.thread.started, {
            param: product,
        });
    };

    const openHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: 'thread' }));
    }, [dispatch]);

    const closeHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: null }));
    }, [dispatch]);

    useEffect(() => {
        hideEcHeaderNavi(isDesktop, isMobile, dispatch);
    }, [isDesktop, isMobile, dispatch]);

    useEffect(() => {
        if (waitFetch) {
            console.log('initializing, waiting fetch');
            return;
        }
        dispatch(
            apiGetActions.thread.started({
                arrowType,
                orderType,
                selectItems: customizeData,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, arrowType, orderType, waitFetch]);

    const testTwoCol = (categoryName: string) => categoryName === '糸';

    return (
        <LayoutContextProvider value={{ testTwoCol, productThumbnailType: 'type-b' }}>
            <OrderSelectBaseLayout
                header={isDesktop && (
                    <OrderPartsImg
                        partsName={partsNameProps}
                        showHelpBtn
                        onHelpClick={openHelp}
                    />
                )}
                body={(
                    <DesignContainer>
                        <CateogryBox
                            categoryId={selectCategoryId}
                            subCategoryId={selectSubCategoryId}
                            categoryData={categoryData}
                            subCategoryData={subCategoryData}
                            categoryChange={categoryChange}
                            subCategoryChange={subCategoryChange}
                        />
                        <OrderOptionContainer />
                        <CategoryLists
                            rootRef={scrollObj}
                            selectedProduct={customizeData.thread}
                            onSelectProduct={onSelectProduct}
                            viewData={viewData}
                            htmlId={htmlId}
                        />
                    </DesignContainer>
                )}
            />
            <OrderPartsHelpThread
                isOpen={isOpenHelp}
                handleOpen={openHelp}
                handleClose={closeHelp}
            />
        </LayoutContextProvider>
    );
};
